import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import LoadingState from '../components/LoadingState';
import ErrorState from '../components/ErrorState';
import { stores } from '../../services/apiClient';

const tabs = ['Resumo', 'Pedidos', 'Produtos', 'Financeiro', 'Auditoria', 'Configuracoes'];
const tabQueryMap = {
  resumo: 'Resumo',
  pedidos: 'Pedidos',
  produtos: 'Produtos',
  financeiro: 'Financeiro',
  auditoria: 'Auditoria',
  configuracoes: 'Configuracoes',
};

function resolveTabFromQuery(value) {
  const key = String(value || '').trim().toLowerCase();
  return tabQueryMap[key] || null;
}

function normalizeOrderStatusFilter(value) {
  const normalized = String(value || '').trim().toLowerCase();
  if (normalized === 'all' || normalized === 'pending' || normalized === 'completed' || normalized === 'cancelled') {
    return normalized;
  }
  return null;
}

function normalizeOrderPaymentFilter(value) {
  const normalized = String(value || '').trim().toLowerCase();
  if (normalized === 'credit_card' || normalized === 'debit_card') {
    return 'card';
  }
  if (normalized === 'cash' || normalized === 'pix' || normalized === 'card' || normalized === 'voucher') {
    return normalized;
  }
  return null;
}

const PAYMENT_METHOD_LABELS = {
  credit_card: 'Cartao de credito',
  debit_card: 'Cartao de debito',
  card: 'Cartao',
  pix: 'PIX',
  cash: 'Dinheiro',
  voucher: 'Voucher',
};

function formatPaymentMethod(value) {
  const key = String(value || '').trim().toLowerCase();
  return PAYMENT_METHOD_LABELS[key] || value || '-';
}

function formatVoucherStatus(value) {
  const normalized = String(value || '').trim().toLowerCase();
  if (normalized === 'approved') return 'Aprovado';
  if (normalized === 'rejected') return 'Rejeitado';
  return 'Pendente';
}

function formatTaxProfileStatus(value) {
  const normalized = String(value || '').trim().toLowerCase();
  if (normalized === 'approved') return 'Aprovado';
  if (normalized === 'rejected') return 'Rejeitado';
  if (normalized === 'pending') return 'Pendente';
  return 'Nao cadastrado';
}

function formatTaxDocumentLabel(documentType, value) {
  const digits = String(value || '').replace(/\D/g, '');
  if (!digits) return '-';
  const suffix = digits.slice(-4);
  const label = String(documentType || '').trim().toLowerCase() === 'cpf' ? 'CPF' : 'CNPJ';
  return `${label} ***${suffix}`;
}

function formatVoucherIssuer(value) {
  const key = String(value || '').trim().toLowerCase();
  if (key === 'ticket') return 'Ticket';
  if (key === 'pluxee') return 'Pluxee';
  if (key === 'biq') return 'Biq';
  if (key === 'unknown') return 'Desconhecido';
  return value || '-';
}

function formatVoucherType(value) {
  const key = String(value || '').trim().toLowerCase();
  if (key === 'alimentacao') return 'Alimentacao';
  if (key === 'refeicao') return 'Refeicao';
  if (key === 'unknown') return 'Desconhecido';
  return value || '-';
}

function formatVoucherBrand(value) {
  const key = String(value || '').trim().toLowerCase();
  if (!key) return '-';
  if (key === 'elo') return 'Elo';
  if (key === 'unknown') return 'Desconhecido';
  return value || '-';
}

function formatVoucherCommissionStatus(value) {
  const key = String(value || '').trim().toLowerCase();
  if (key === 'pending') return 'Pendente';
  if (key === 'invoiced') return 'Faturada';
  if (key === 'paid') return 'Paga';
  if (key === 'overdue') return 'Vencida';
  return value || '-';
}

function resolveVoucherMetaLabel(order) {
  const issuer = order?.voucher_issuer || order?.voucher?.voucher_issuer || order?.voucher?.issuer;
  const type = order?.voucher_type || order?.voucher?.voucher_type || order?.voucher?.type;
  const brand = order?.voucher_brand || order?.voucher?.voucher_brand || order?.voucher?.brand;
  if (!issuer && !type && !brand) return '-';
  return `${formatVoucherIssuer(issuer)} / ${formatVoucherType(type)} / ${formatVoucherBrand(brand)}`;
}

function formatPaymentStatus(value) {
  const key = String(value || '').trim().toLowerCase();
  if (!key) return '-';
  if (key === 'approved') return 'Aprovado';
  if (key === 'rejected') return 'Recusado';
  if (key === 'pending' || key === 'pending_cash') return 'Pendente';
  if (key === 'cancelled') return 'Cancelado';
  if (key === 'error' || key === 'technical_failure' || key === 'pending_error') return 'Falha tecnica';
  return value || '-';
}

function formatMoney(value) {
  return `R$ ${Number(value || 0).toFixed(2)}`;
}

function formatDate(value) {
  if (!value) return '-';
  try {
    return new Date(value).toLocaleString('pt-BR');
  } catch {
    return String(value);
  }
}

export default function StoreDetails() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('Resumo');
  const [store, setStore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [tabLoading, setTabLoading] = useState(false);
  const [tabError, setTabError] = useState('');

  const [ordersFilters, setOrdersFilters] = useState({ status: 'all', payment: '' });
  const [productsFilters, setProductsFilters] = useState({ q: '', available: '' });
  const [voucherCommissionsFilters, setVoucherCommissionsFilters] = useState({ status: 'all' });

  const [ordersData, setOrdersData] = useState(null);
  const [productsData, setProductsData] = useState(null);
  const [financeData, setFinanceData] = useState(null);
  const [voucherCommissionsData, setVoucherCommissionsData] = useState(null);
  const [auditData, setAuditData] = useState(null);
  const [settingsData, setSettingsData] = useState(null);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [voucherReviewLoading, setVoucherReviewLoading] = useState(false);
  const [taxProfileReviewLoading, setTaxProfileReviewLoading] = useState(false);
  const [settingsForm, setSettingsForm] = useState({ fee_percent_override: '', payout_schedule: 'weekly' });
  const [debtActionLoading, setDebtActionLoading] = useState({});

  useEffect(() => {
    const nextTab = resolveTabFromQuery(searchParams.get('tab'));
    const nextStatus = normalizeOrderStatusFilter(searchParams.get('status'));
    const nextPayment = normalizeOrderPaymentFilter(searchParams.get('payment'));
    const hasStatusParam = searchParams.has('status');
    const hasPaymentParam = searchParams.has('payment');

    if (nextTab) {
      setActiveTab(nextTab);
    }

    if (hasStatusParam || hasPaymentParam) {
      setOrdersFilters((prev) => ({
        ...prev,
        ...(hasStatusParam ? { status: nextStatus || 'all' } : {}),
        ...(hasPaymentParam ? { payment: nextPayment || '' } : (hasStatusParam ? { payment: '' } : {})),
      }));
    }
  }, [searchParams]);

  useEffect(() => {
    let mounted = true;
    async function loadStore() {
      setLoading(true);
      setError('');
      try {
        const data = await stores.getById(id);
        if (mounted) setStore(data);
      } catch {
        if (mounted) setError('Erro ao carregar loja');
      } finally {
        if (mounted) setLoading(false);
      }
    }
    loadStore();
    return () => { mounted = false; };
  }, [id]);

  useEffect(() => {
    if (!id || !store) return;
    let mounted = true;

    async function loadTabData() {
      if (activeTab === 'Resumo') return;
      setTabLoading(true);
      setTabError('');
      try {
        if (activeTab === 'Pedidos') {
          const data = await stores.getOrders(id, {
            status: ordersFilters.status,
            payment: ordersFilters.payment || undefined
          });
          if (mounted) setOrdersData(data);
        } else if (activeTab === 'Produtos') {
          const data = await stores.getProducts(id, {
            q: productsFilters.q || undefined,
            available: productsFilters.available
          });
          if (mounted) setProductsData(data);
        } else if (activeTab === 'Financeiro') {
          const [finance, voucherCommissions] = await Promise.all([
            stores.getFinance(id),
            stores.getVoucherCommissions(id, {
              status: voucherCommissionsFilters.status,
              limit: 500,
            }).catch(() => null),
          ]);
          if (mounted) {
            setFinanceData(finance);
            setVoucherCommissionsData(voucherCommissions);
          }
        } else if (activeTab === 'Auditoria') {
          const data = await stores.getAudit(id, { limit: 300 });
          if (mounted) setAuditData(data);
        } else if (activeTab === 'Configuracoes') {
          const [data, finance] = await Promise.all([
            stores.getSettings(id),
            stores.getFinance(id),
          ]);
          if (mounted) {
            setSettingsData(data);
            setFinanceData(finance);
            setVoucherCommissionsData(null);
            setSettingsForm({
              fee_percent_override: data?.fee_percent_override == null ? '' : String(data.fee_percent_override),
              payout_schedule: data?.payout_schedule || 'weekly'
            });
          }
        }
      } catch {
        if (mounted) setTabError(`Erro ao carregar ${activeTab.toLowerCase()}`);
      } finally {
        if (mounted) setTabLoading(false);
      }
    }

    loadTabData();
    return () => { mounted = false; };
  }, [activeTab, id, store, ordersFilters.status, ordersFilters.payment, productsFilters.q, productsFilters.available, voucherCommissionsFilters.status]);

  const summaryCards = useMemo(() => {
    if (!store) return [];
    return [
      { label: 'GMV dia', value: formatMoney(store.metrics?.gmv_day) },
      { label: 'GMV mes', value: formatMoney(store.metrics?.gmv_month) },
      { label: 'GMV ano', value: formatMoney(store.metrics?.gmv_year) },
      { label: 'Pedidos dia', value: Number(store.metrics?.orders_day || 0) },
      { label: 'Ticket medio', value: formatMoney(store.metrics?.avg_ticket) },
      { label: 'Taxa atual', value: `${Number(store.fee_percent || 0).toFixed(2)}%` },
    ];
  }, [store]);

  const pendingCashDebtOrders = useMemo(
    () => (financeData?.orders || []).filter(
      (order) => String(order.payment_method || '').toLowerCase() === 'cash' && ['pending', 'scheduled'].includes(order.cash_debt_status || 'pending')
    ),
    [financeData]
  );

  const voucherSettings = settingsData?.voucher || {};
  const taxProfile = settingsData?.tax_profile || store?.tax_profile || {};
  const voucherIssuersLabel = (Array.isArray(voucherSettings.accepted_issuers) ? voucherSettings.accepted_issuers : [])
    .filter(Boolean)
    .join(', ');
  const voucherTypesLabel = (Array.isArray(voucherSettings.accepted_types) ? voucherSettings.accepted_types : [])
    .filter(Boolean)
    .join(', ');
  const voucherCommissionsSummary = voucherCommissionsData?.summary
    || financeData?.voucher_commissions?.summary
    || settingsData?.voucher_commissions?.summary
    || {};
  const voucherCommissionsRows = Array.isArray(voucherCommissionsData?.commissions)
    ? voucherCommissionsData.commissions
    : [];
  const orderNumberById = useMemo(() => {
    const map = {};
    (financeData?.orders || []).forEach((order) => {
      map[String(order?.id || '')] = String(order?.order_number || '-');
    });
    (ordersData?.orders || []).forEach((order) => {
      map[String(order?.id || '')] = String(order?.order_number || '-');
    });
    return map;
  }, [financeData, ordersData]);

  const handleSaveSettings = async () => {
    setSettingsSaving(true);
    try {
      const payload = {
        payout_schedule: settingsForm.payout_schedule,
        fee_percent_override: settingsForm.fee_percent_override === '' ? null : Number(settingsForm.fee_percent_override),
      };
      await stores.updateSettings(id, payload);
      const [updatedStore, updatedSettings, updatedFinance] = await Promise.all([
        stores.getById(id),
        stores.getSettings(id),
        stores.getFinance(id),
      ]);
      setStore(updatedStore);
      setSettingsData(updatedSettings);
      setFinanceData(updatedFinance);
      setSettingsForm({
        fee_percent_override: updatedSettings?.fee_percent_override == null ? '' : String(updatedSettings.fee_percent_override),
        payout_schedule: updatedSettings?.payout_schedule || 'weekly'
      });
      alert('Configuracoes da loja atualizadas.');
    } catch (err) {
      alert(err?.response?.data?.detail || 'Nao foi possivel salvar configuracoes.');
    } finally {
      setSettingsSaving(false);
    }
  };

  const handleDebtAction = async (orderId, action) => {
    let dueDate = null;
    if (action === 'schedule') {
      dueDate = prompt('Data da cobranca (YYYY-MM-DD). Opcional:', '') || null;
    }
    const note = prompt('Observacao da acao (opcional):', '') || null;
    setDebtActionLoading((prev) => ({ ...prev, [orderId]: true }));
    try {
      await stores.updateCashDebt(id, orderId, { action, due_date: dueDate, note });
      const [updatedFinance, updatedOrders, updatedSettings] = await Promise.all([
        stores.getFinance(id),
        stores.getOrders(id, { status: ordersFilters.status, payment: ordersFilters.payment || undefined }),
        stores.getSettings(id),
      ]);
      setFinanceData(updatedFinance);
      setOrdersData(updatedOrders);
      setSettingsData(updatedSettings);
      alert('Status da cobranca atualizado.');
    } catch (err) {
      alert(err?.response?.data?.detail || 'Nao foi possivel atualizar cobranca.');
    } finally {
      setDebtActionLoading((prev) => ({ ...prev, [orderId]: false }));
    }
  };

  const handleRefreshVoucherCommissions = async () => {
    if (!id) return;
    try {
      const data = await stores.getVoucherCommissions(id, {
        status: voucherCommissionsFilters.status,
        limit: 500,
      });
      setVoucherCommissionsData(data);
    } catch (_) {
      // Mantem os dados atuais em caso de erro.
    }
  };

  const handleVoucherReview = async (nextStatus) => {
    if (!id) return;

    const normalized = String(nextStatus || '').trim().toLowerCase();
    if (!['pending', 'approved', 'rejected'].includes(normalized)) {
      return;
    }

    let rejectionReason = null;
    if (normalized === 'rejected') {
      rejectionReason = prompt('Motivo da reprovacao (obrigatorio):', '') || '';
      if (!String(rejectionReason || '').trim()) {
        alert('Motivo obrigatorio para reprovar voucher.');
        return;
      }
    }

    setVoucherReviewLoading(true);
    try {
      await stores.reviewVoucher(id, {
        approval_status: normalized,
        rejection_reason: normalized === 'rejected' ? String(rejectionReason).trim() : null,
      });
      const updatedSettings = await stores.getSettings(id);
      setSettingsData(updatedSettings);
      alert(`Status de voucher atualizado para ${formatVoucherStatus(normalized)}.`);
    } catch (err) {
      alert(err?.response?.data?.detail || 'Nao foi possivel atualizar status de voucher.');
    } finally {
      setVoucherReviewLoading(false);
    }
  };

  const handleTaxProfileReview = async (nextStatus) => {
    if (!id) return;
    const normalized = String(nextStatus || '').trim().toLowerCase();
    if (!['pending', 'approved', 'rejected'].includes(normalized)) {
      return;
    }

    let rejectionReason = null;
    if (normalized === 'rejected') {
      rejectionReason = prompt('Motivo da reprovacao fiscal (obrigatorio):', '') || '';
      if (!String(rejectionReason || '').trim()) {
        alert('Motivo obrigatorio para reprovar perfil fiscal.');
        return;
      }
    }

    setTaxProfileReviewLoading(true);
    try {
      await stores.reviewTaxProfile(id, {
        status: normalized,
        rejection_reason: normalized === 'rejected' ? String(rejectionReason).trim() : null,
      });
      const [updatedStore, updatedSettings] = await Promise.all([
        stores.getById(id),
        stores.getSettings(id),
      ]);
      setStore(updatedStore);
      setSettingsData(updatedSettings);
      alert(`Status fiscal atualizado para ${formatTaxProfileStatus(normalized)}.`);
    } catch (err) {
      alert(err?.response?.data?.detail || 'Nao foi possivel atualizar perfil fiscal.');
    } finally {
      setTaxProfileReviewLoading(false);
    }
  };

  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} />;
  if (!store) return null;

  return (
    <div className="admin-section">
      <PageHeader title={store.name} subtitle={`Status: ${store.status}`} />

      <div className="admin-tabs">
        {tabs.map((tab) => (
          <button
            key={tab}
            className={activeTab === tab ? 'active' : ''}
            onClick={() => setActiveTab(tab)}
          >
            {tab}
          </button>
        ))}
      </div>

      {activeTab === 'Resumo' && (
        <div className="admin-grid">
          {summaryCards.map((item) => (
            <div key={item.label} className="admin-card">
              <h3>{item.label}</h3>
              <div className="value">{item.value}</div>
            </div>
          ))}
        </div>
      )}

      {activeTab === 'Pedidos' && (
        <div className="admin-card">
          <div className="admin-filters" style={{ marginBottom: 12 }}>
            <select
              className="admin-select"
              value={ordersFilters.status}
              onChange={(e) => setOrdersFilters((prev) => ({ ...prev, status: e.target.value }))}
            >
              <option value="all">Todos</option>
              <option value="pending">Pendentes</option>
              <option value="completed">Finalizados</option>
              <option value="cancelled">Cancelados</option>
            </select>
            <select
              className="admin-select"
              value={ordersFilters.payment}
              onChange={(e) => setOrdersFilters((prev) => ({ ...prev, payment: e.target.value }))}
            >
              <option value="">Todos pagamentos</option>
              <option value="cash">Dinheiro</option>
              <option value="pix">PIX</option>
              <option value="card">Cartao</option>
              <option value="voucher">Voucher</option>
            </select>
            <button className="admin-button secondary" onClick={() => setOrdersFilters((prev) => ({ ...prev }))}>
              Atualizar
            </button>
          </div>

          {tabLoading ? <LoadingState /> : null}
          {tabError ? <ErrorState message={tabError} /> : null}

          {!tabLoading && !tabError && ordersData && (
            <>
              <div className="admin-grid" style={{ marginBottom: 14 }}>
                <div className="admin-card"><h3>Pendentes</h3><div className="value">{ordersData.summary?.pending || 0}</div></div>
                <div className="admin-card"><h3>Finalizados</h3><div className="value">{ordersData.summary?.completed || 0}</div></div>
                <div className="admin-card"><h3>Cancelados</h3><div className="value">{ordersData.summary?.cancelled || 0}</div></div>
                <div className="admin-card"><h3>Total</h3><div className="value">{ordersData.summary?.all || 0}</div></div>
                <div className="admin-card"><h3>Dinheiro</h3><div className="value">{ordersData.summary?.by_payment?.cash || 0}</div></div>
                <div className="admin-card"><h3>PIX</h3><div className="value">{ordersData.summary?.by_payment?.pix || 0}</div></div>
                <div className="admin-card"><h3>Cartao</h3><div className="value">{ordersData.summary?.by_payment?.card || 0}</div></div>
                <div className="admin-card"><h3>Voucher</h3><div className="value">{ordersData.summary?.by_payment?.voucher || 0}</div></div>
                <div className="admin-card"><h3>Finalizados com Correfood</h3><div className="value">{ordersData.summary?.completed_platform || 0}</div></div>
                <div className="admin-card"><h3>Finalizados com motoboy proprio</h3><div className="value">{ordersData.summary?.completed_own || 0}</div></div>
                <div className="admin-card"><h3>Repasse pendente</h3><div className="value">{formatMoney(ordersData.summary?.store_payout_pending || 0)}</div></div>
                <div className="admin-card"><h3>Divida futura (cash)</h3><div className="value">{formatMoney(ordersData.summary?.cash_future_fee_pending || 0)}</div></div>
              </div>
              <div className="admin-fees-table-wrap">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Pedido</th>
                      <th>Status</th>
                      <th>Pagamento</th>
                      <th>Status pagamento</th>
                      <th>Voucher (emissor/tipo/bandeira)</th>
                      <th>Entrega</th>
                      <th>Motoboy</th>
                      <th>Total</th>
                      <th>Taxa admin</th>
                      <th>Repasse loja</th>
                      <th>Divida cash</th>
                      <th>Criado em</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(ordersData.orders || []).map((order) => (
                      <tr key={order.id}>
                        <td>
                          <div style={{ fontWeight: 700 }}>{order.order_number}</div>
                          <div style={{ color: '#B8B8C5', fontSize: 12 }}>
                            {(order.items || []).slice(0, 2).map((item) => `${item.quantity}x ${item.product_name}`).join(' | ') || '-'}
                          </div>
                        </td>
                        <td>{order.status}</td>
                        <td>{formatPaymentMethod(order.payment_method)}</td>
                        <td>{formatPaymentStatus(order.payment_status)}</td>
                        <td>{resolveVoucherMetaLabel(order)}</td>
                        <td>{order.delivery_assignment_label || '-'}</td>
                        <td>{order.courier_name || '-'}</td>
                        <td>{formatMoney(order.total)}</td>
                        <td>{formatMoney(order.admin_fee)}</td>
                        <td>
                          {order.store_payout?.status
                            ? `${formatMoney(order.store_payout.amount)} (${order.store_payout.status})`
                            : '-'}
                        </td>
                        <td>
                          {String(order.payment_method || '').toLowerCase() === 'cash'
                            ? `${formatMoney(order.admin_fee)} (${order.cash_debt_status || 'pending'})`
                            : '-'}
                        </td>
                        <td>{formatDate(order.created_at)}</td>
                      </tr>
                    ))}
                    {!(ordersData.orders || []).length && (
                      <tr>
                        <td colSpan={12} style={{ textAlign: 'center', color: '#B8B8C5' }}>
                          Nenhum pedido encontrado.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {activeTab === 'Produtos' && (
        <div className="admin-card">
          <div className="admin-filters" style={{ marginBottom: 12 }}>
            <input
              className="admin-input"
              placeholder="Buscar produto"
              value={productsFilters.q}
              onChange={(e) => setProductsFilters((prev) => ({ ...prev, q: e.target.value }))}
            />
            <select
              className="admin-select"
              value={productsFilters.available}
              onChange={(e) => setProductsFilters((prev) => ({ ...prev, available: e.target.value }))}
            >
              <option value="">Todos</option>
              <option value="true">Disponiveis</option>
              <option value="false">Indisponiveis</option>
            </select>
          </div>

          {tabLoading ? <LoadingState /> : null}
          {tabError ? <ErrorState message={tabError} /> : null}

          {!tabLoading && !tabError && productsData && (
            <>
              <div className="admin-grid" style={{ marginBottom: 14 }}>
                <div className="admin-card"><h3>Total produtos</h3><div className="value">{productsData.summary?.total || 0}</div></div>
                <div className="admin-card"><h3>Disponiveis</h3><div className="value">{productsData.summary?.available || 0}</div></div>
                <div className="admin-card"><h3>Indisponiveis</h3><div className="value">{productsData.summary?.unavailable || 0}</div></div>
              </div>
              <div className="admin-fees-table-wrap">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Produto</th>
                      <th>Preco</th>
                      <th>Status</th>
                      <th>Adicionais</th>
                      <th>Atualizado em</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(productsData.products || []).map((product) => (
                      <tr key={product.id}>
                        <td>
                          <div style={{ fontWeight: 700 }}>{product.name}</div>
                          <div style={{ color: '#B8B8C5', fontSize: 12 }}>{product.description || 'Sem descricao'}</div>
                        </td>
                        <td>{formatMoney(product.price)}</td>
                        <td>{product.available ? 'Ativo' : 'Inativo'}</td>
                        <td>{product.extras_count}</td>
                        <td>{formatDate(product.updated_at)}</td>
                      </tr>
                    ))}
                    {!(productsData.products || []).length && (
                      <tr>
                        <td colSpan={5} style={{ textAlign: 'center', color: '#B8B8C5' }}>
                          Nenhum produto encontrado.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {activeTab === 'Financeiro' && (
        <div className="admin-card">
          {tabLoading ? <LoadingState /> : null}
          {tabError ? <ErrorState message={tabError} /> : null}

          {!tabLoading && !tabError && financeData && (
            <>
              <div className="admin-filters" style={{ marginBottom: 12 }}>
                <select
                  className="admin-select"
                  value={voucherCommissionsFilters.status}
                  onChange={(e) => setVoucherCommissionsFilters((prev) => ({ ...prev, status: e.target.value }))}
                >
                  <option value="all">Comissao voucher: todos</option>
                  <option value="pending">Pendente</option>
                  <option value="invoiced">Faturada</option>
                  <option value="paid">Paga</option>
                  <option value="overdue">Vencida</option>
                </select>
                <button className="admin-button secondary" onClick={handleRefreshVoucherCommissions}>
                  Atualizar comissoes
                </button>
              </div>

              <div className="admin-grid" style={{ marginBottom: 14 }}>
                <div className="admin-card"><h3>Valor bruto</h3><div className="value">{formatMoney(financeData.totals?.gross_revenue)}</div></div>
                <div className="admin-card"><h3>Taxa admin</h3><div className="value">{formatMoney(financeData.totals?.admin_fee)}</div></div>
                <div className="admin-card"><h3>Valor liquido (repasse)</h3><div className="value">{formatMoney(financeData.totals?.net_repass)}</div></div>
                <div className="admin-card"><h3>Repasse pendente</h3><div className="value">{formatMoney(financeData.totals?.store_payout_pending)}</div></div>
                <div className="admin-card"><h3>Repasse pago</h3><div className="value">{formatMoney(financeData.totals?.store_payout_paid)}</div></div>
                <div className="admin-card"><h3>Taxa da loja</h3><div className="value">{Number(financeData.fee_percent_effective || 0).toFixed(2)}%</div></div>
                <div className="admin-card"><h3>Frete proprio da loja</h3><div className="value">{formatMoney(financeData.totals?.delivery_fee_own)}</div></div>
                <div className="admin-card"><h3>Frete Correfood excluido</h3><div className="value">{formatMoney(financeData.totals?.delivery_fee_platform_excluded)}</div></div>
                <div className="admin-card"><h3>Divida cash pendente</h3><div className="value">{formatMoney(financeData.totals?.cash_debt_pending + financeData.totals?.cash_debt_scheduled)}</div></div>
                <div className="admin-card"><h3>Comissao voucher (total)</h3><div className="value">{formatMoney(voucherCommissionsSummary.commission_amount)}</div></div>
                <div className="admin-card"><h3>Comissao voucher pendente</h3><div className="value">{formatMoney(voucherCommissionsSummary.by_status?.pending?.commission_amount)}</div></div>
                <div className="admin-card"><h3>Comissao voucher vencida</h3><div className="value">{formatMoney(voucherCommissionsSummary.by_status?.overdue?.commission_amount)}</div></div>
              </div>

              <div className="admin-fees-table-wrap">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Pedido</th>
                      <th>Pagamento</th>
                      <th>Status pagamento</th>
                      <th>Voucher (emissor/tipo/bandeira)</th>
                      <th>Entrega</th>
                      <th>Motoboy</th>
                      <th>Bruto</th>
                      <th>Taxa admin</th>
                      <th>Liquido</th>
                      <th>Repasse</th>
                      <th>Divida cash</th>
                      <th>Acoes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(financeData.orders || []).map((order) => {
                      const isCash = String(order.payment_method || '').toLowerCase() === 'cash';
                      const loadingDebt = !!debtActionLoading[order.id];
                      return (
                        <tr key={order.id}>
                          <td>{order.order_number}</td>
                          <td>{formatPaymentMethod(order.payment_method)}</td>
                          <td>{formatPaymentStatus(order.payment_status)}</td>
                          <td>{resolveVoucherMetaLabel(order)}</td>
                          <td>{order.delivery_assignment_label || '-'}</td>
                          <td>{order.courier_name || '-'}</td>
                          <td>{formatMoney(order.gross_store)}</td>
                          <td>{formatMoney(order.admin_fee)}</td>
                          <td>{formatMoney(order.net_store)}</td>
                          <td>
                            {order.store_payout?.status
                              ? `${formatMoney(order.store_payout.amount)} (${order.store_payout.status})`
                              : '-'}
                          </td>
                          <td>
                            {isCash
                              ? `${formatMoney(order.cash_debt)} (${order.cash_debt_status || 'pending'})`
                              : '-'}
                          </td>
                          <td>
                            {isCash ? (
                              <div className="admin-fees-actions">
                                <button className="admin-button secondary" onClick={() => handleDebtAction(order.id, 'schedule')} disabled={loadingDebt}>
                                  Agendar
                                </button>
                                <button className="admin-button secondary" onClick={() => handleDebtAction(order.id, 'collect')} disabled={loadingDebt}>
                                  Cobrado
                                </button>
                                <button className="admin-button secondary" onClick={() => handleDebtAction(order.id, 'cancel')} disabled={loadingDebt}>
                                  Cancelar
                                </button>
                              </div>
                            ) : '-'}
                          </td>
                        </tr>
                      );
                    })}
                    {!(financeData.orders || []).length && (
                      <tr>
                        <td colSpan={12} style={{ textAlign: 'center', color: '#B8B8C5' }}>
                          Nenhum dado financeiro encontrado.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="admin-fees-table-wrap" style={{ marginTop: 14 }}>
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Pedido</th>
                      <th>Status comissao</th>
                      <th>Valor bruto</th>
                      <th>Comissao Correfood</th>
                      <th>Liquido estimado loja</th>
                      <th>Vencimento</th>
                      <th>Faturada em</th>
                      <th>Paga em</th>
                    </tr>
                  </thead>
                  <tbody>
                    {voucherCommissionsRows.map((commission) => (
                      <tr key={commission.id}>
                        <td>{orderNumberById[String(commission.order_id || '')] || commission.order_id || '-'}</td>
                        <td>{formatVoucherCommissionStatus(commission.status)}</td>
                        <td>{formatMoney(commission.gross_amount)}</td>
                        <td>{formatMoney(commission.commission_amount)}</td>
                        <td>{formatMoney(commission.merchant_net_amount)}</td>
                        <td>{commission.due_date || '-'}</td>
                        <td>{formatDate(commission.invoiced_at)}</td>
                        <td>{formatDate(commission.paid_at)}</td>
                      </tr>
                    ))}
                    {!voucherCommissionsRows.length && (
                      <tr>
                        <td colSpan={8} style={{ textAlign: 'center', color: '#B8B8C5' }}>
                          Nenhuma comissao de voucher para o filtro atual.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {activeTab === 'Auditoria' && (
        <div className="admin-card">
          {tabLoading ? <LoadingState /> : null}
          {tabError ? <ErrorState message={tabError} /> : null}

          {!tabLoading && !tabError && auditData && (
            <div className="admin-fees-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Data</th>
                    <th>Usuario</th>
                    <th>Acao</th>
                    <th>Entidade</th>
                    <th>Detalhes</th>
                  </tr>
                </thead>
                <tbody>
                  {(auditData.logs || []).map((log) => (
                    <tr key={log.id}>
                      <td>{formatDate(log.created_at)}</td>
                      <td>{log.actor_email || '-'}</td>
                      <td>{log.action}</td>
                      <td>{log.entity_type}</td>
                      <td>
                        <pre style={{ margin: 0, whiteSpace: 'pre-wrap', color: '#B8B8C5', fontSize: 12 }}>
                          {JSON.stringify(log.details || {}, null, 2)}
                        </pre>
                      </td>
                    </tr>
                  ))}
                  {!(auditData.logs || []).length && (
                    <tr>
                      <td colSpan={5} style={{ textAlign: 'center', color: '#B8B8C5' }}>
                        Nenhum log de auditoria para esta loja.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === 'Configuracoes' && (
        <div className="admin-card">
          {tabLoading ? <LoadingState /> : null}
          {tabError ? <ErrorState message={tabError} /> : null}

          {!tabLoading && !tabError && settingsData && (
            <>
              <div className="admin-grid" style={{ marginBottom: 14 }}>
                <div className="admin-card"><h3>Taxa global</h3><div className="value">{Number(settingsData.global_fee_percent || 0).toFixed(2)}%</div></div>
                <div className="admin-card"><h3>Taxa override loja</h3><div className="value">{settingsData.fee_percent_override == null ? 'Nao definida' : `${Number(settingsData.fee_percent_override).toFixed(2)}%`}</div></div>
                <div className="admin-card"><h3>Taxa efetiva</h3><div className="value">{Number(settingsData.fee_percent_effective || 0).toFixed(2)}%</div></div>
                <div className="admin-card"><h3>Divida futura cash</h3><div className="value">{formatMoney(settingsData.cash_future_fee_pending)}</div></div>
                <div className="admin-card"><h3>Voucher</h3><div className="value">{voucherSettings.accepts_voucher ? 'Habilitado' : 'Desabilitado'}</div></div>
                <div className="admin-card"><h3>Status voucher</h3><div className="value">{formatVoucherStatus(voucherSettings.approval_status)}</div></div>
                <div className="admin-card"><h3>Status fiscal</h3><div className="value">{formatTaxProfileStatus(taxProfile.status)}</div></div>
                <div className="admin-card"><h3>Fiscal completo</h3><div className="value">{taxProfile.is_complete ? 'Sim' : 'Nao'}</div></div>
                <div className="admin-card"><h3>Comissao voucher pendente</h3><div className="value">{formatMoney(voucherCommissionsSummary.by_status?.pending?.commission_amount)}</div></div>
                <div className="admin-card"><h3>Comissao voucher vencida</h3><div className="value">{formatMoney(voucherCommissionsSummary.by_status?.overdue?.commission_amount)}</div></div>
              </div>

              <div className="admin-card" style={{ marginBottom: 14 }}>
                <h3>Perfil fiscal da loja</h3>
                <div style={{ color: '#B8B8C5', marginTop: 8 }}>
                  <p><strong>Status:</strong> {formatTaxProfileStatus(taxProfile.status)}</p>
                  <p>
                    <strong>Tipo de pessoa:</strong>{' '}
                    {taxProfile.person_type
                      ? (String(taxProfile.person_type || '').toLowerCase() === 'pf' ? 'Pessoa fisica' : 'Pessoa juridica')
                      : '-'}
                  </p>
                  <p><strong>Documento:</strong> {formatTaxDocumentLabel(taxProfile.document_type, taxProfile.document_number || taxProfile.document_number_masked)}</p>
                  <p><strong>Razao social / nome:</strong> {taxProfile.legal_name || '-'}</p>
                  <p><strong>Nome fantasia:</strong> {taxProfile.trade_name || '-'}</p>
                  <p><strong>CNAE principal:</strong> {taxProfile.cnae_primary || '-'}</p>
                  {taxProfile.rejection_reason ? (
                    <p><strong>Motivo da reprovacao:</strong> {taxProfile.rejection_reason}</p>
                  ) : null}
                  {taxProfile.reviewed_at ? (
                    <p><strong>Revisado em:</strong> {formatDate(taxProfile.reviewed_at)}</p>
                  ) : null}
                </div>
                <div className="admin-filters" style={{ marginTop: 10 }}>
                  <button
                    className="admin-button secondary"
                    onClick={() => handleTaxProfileReview('approved')}
                    disabled={taxProfileReviewLoading}
                  >
                    {taxProfileReviewLoading ? 'Processando...' : 'Aprovar fiscal'}
                  </button>
                  <button
                    className="admin-button secondary"
                    onClick={() => handleTaxProfileReview('rejected')}
                    disabled={taxProfileReviewLoading}
                  >
                    {taxProfileReviewLoading ? 'Processando...' : 'Reprovar fiscal'}
                  </button>
                  <button
                    className="admin-button secondary"
                    onClick={() => handleTaxProfileReview('pending')}
                    disabled={taxProfileReviewLoading}
                  >
                    {taxProfileReviewLoading ? 'Processando...' : 'Voltar fiscal para pendente'}
                  </button>
                </div>
              </div>

              <div className="admin-card" style={{ marginBottom: 14 }}>
                <h3>Configuracao de voucher da loja</h3>
                <div style={{ color: '#B8B8C5', marginTop: 8 }}>
                  <p><strong>Aceita voucher:</strong> {voucherSettings.accepts_voucher ? 'Sim' : 'Nao'}</p>
                  <p><strong>Status de aprovacao:</strong> {formatVoucherStatus(voucherSettings.approval_status)}</p>
                  <p><strong>Emissores:</strong> {voucherIssuersLabel || '-'}</p>
                  <p><strong>Tipos:</strong> {voucherTypesLabel || '-'}</p>
                  <p><strong>ID no parceiro:</strong> {voucherSettings.partner_store_id || '-'}</p>
                  <p><strong>Liberado no checkout:</strong> {voucherSettings.enabled_for_checkout ? 'Sim' : 'Nao'}</p>
                  {voucherSettings.rejection_reason ? (
                    <p><strong>Motivo da reprovacao:</strong> {voucherSettings.rejection_reason}</p>
                  ) : null}
                  {voucherSettings.reviewed_at ? (
                    <p><strong>Revisado em:</strong> {formatDate(voucherSettings.reviewed_at)}</p>
                  ) : null}
                </div>
                <div className="admin-filters" style={{ marginTop: 10 }}>
                  <button
                    className="admin-button secondary"
                    onClick={() => handleVoucherReview('approved')}
                    disabled={voucherReviewLoading}
                  >
                    {voucherReviewLoading ? 'Processando...' : 'Aprovar voucher'}
                  </button>
                  <button
                    className="admin-button secondary"
                    onClick={() => handleVoucherReview('rejected')}
                    disabled={voucherReviewLoading}
                  >
                    {voucherReviewLoading ? 'Processando...' : 'Reprovar voucher'}
                  </button>
                  <button
                    className="admin-button secondary"
                    onClick={() => handleVoucherReview('pending')}
                    disabled={voucherReviewLoading}
                  >
                    {voucherReviewLoading ? 'Processando...' : 'Voltar para pendente'}
                  </button>
                </div>
              </div>

              <div className="admin-filters">
                <input
                  className="admin-input"
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  placeholder="Taxa override (%) - deixe vazio para usar global"
                  value={settingsForm.fee_percent_override}
                  onChange={(e) => setSettingsForm((prev) => ({ ...prev, fee_percent_override: e.target.value }))}
                />
                <select
                  className="admin-select"
                  value={settingsForm.payout_schedule}
                  onChange={(e) => setSettingsForm((prev) => ({ ...prev, payout_schedule: e.target.value }))}
                >
                  <option value="daily">Repasse diario</option>
                  <option value="weekly">Repasse semanal</option>
                  <option value="monthly">Repasse mensal</option>
                </select>
                <button className="admin-button" onClick={handleSaveSettings} disabled={settingsSaving}>
                  {settingsSaving ? 'Salvando...' : 'Salvar configuracoes'}
                </button>
              </div>

              <p style={{ marginTop: 12, color: '#B8B8C5' }}>
                Pedidos pagos em dinheiro nao descontam taxa no momento da venda. O valor vira cobranca futura para o lojista e pode ser agendado, marcado como cobrado ou cancelado na aba Financeiro.
              </p>

              <div className="admin-fees-table-wrap" style={{ marginTop: 14 }}>
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Pedido cash com cobranca futura</th>
                      <th>Valor da taxa</th>
                      <th>Status</th>
                      <th>Vencimento</th>
                      <th>Acoes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingCashDebtOrders.map((order) => {
                      const loadingDebt = !!debtActionLoading[order.id];
                      return (
                        <tr key={order.id}>
                          <td>{order.order_number}</td>
                          <td>{formatMoney(order.cash_debt)}</td>
                          <td>{order.cash_debt_status || 'pending'}</td>
                          <td>{order.cash_debt_due_date || '-'}</td>
                          <td>
                            <div className="admin-fees-actions">
                              <button className="admin-button secondary" onClick={() => handleDebtAction(order.id, 'schedule')} disabled={loadingDebt}>
                                Agendar
                              </button>
                              <button className="admin-button secondary" onClick={() => handleDebtAction(order.id, 'collect')} disabled={loadingDebt}>
                                Cobrado
                              </button>
                              <button className="admin-button secondary" onClick={() => handleDebtAction(order.id, 'cancel')} disabled={loadingDebt}>
                                Cancelar
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                    {!pendingCashDebtOrders.length && (
                      <tr>
                        <td colSpan={5} style={{ textAlign: 'center', color: '#B8B8C5' }}>
                          Nenhuma cobranca futura em dinheiro pendente.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
